import React, { useState, useMemo } from 'react';
import { Upload, File, Trash2, Eye, Copy, Link as LinkIcon } from 'lucide-react';
import { useToast } from './Toast';
import FilePreviewModal from './FilePreviewModal';

const FileHandler = ({ files, setFiles, onUpload, onDelete }) => {
  const { showToast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const uniqueFiles = useMemo(() => {
      const seenPaths = new Set();
      return files.filter(file => {
          if (seenPaths.has(file.path)) return false;
          seenPaths.add(file.path);
          return true;
      });
  }, [files]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await onUpload(file, null, 'General');
    } catch (err) {
      showToast('Upload Failed', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-800 text-lg">Project Documents</h3>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">{uniqueFiles.length} unique</span>
            </div>
            <label className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer ${isUploading ? 'opacity-50' : ''}`}>
                <Upload size={18} />
                <span>{isUploading ? '...' : 'Upload General File'}</span>
                <input type="file" onChange={handleFileUpload} className="hidden" disabled={isUploading} />
            </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {uniqueFiles.map(file => (
                <div key={file.id} className="p-4 border rounded-lg bg-gray-50 flex items-start gap-3 hover:shadow-md transition-shadow">
                    <div className="bg-white p-2 rounded border cursor-pointer" onClick={() => setPreviewFile(file)}>
                        <File size={24} className="text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-gray-800 cursor-pointer hover:text-blue-600" onClick={() => setPreviewFile(file)} title={file.name}>{file.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] uppercase font-bold text-gray-400">{file.type ? file.type.split('/')[1] : 'FILE'}</span>
                            <span className="text-[10px] text-gray-400">• {file.uploadDate}</span>
                        </div>
                        
                        <div className="flex gap-3 mt-3">
                            <button onClick={() => setPreviewFile(file)} className="text-xs text-blue-600 flex gap-1 font-bold hover:underline">
                                <Eye size={12} /> View
                            </button>
                            <button onClick={() => onDelete(file.id)} className="text-xs text-red-600 flex gap-1 font-bold hover:text-red-800">
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                    
                    {files.filter(f => f.path === file.path).length > 1 && (
                        <div className="flex flex-col items-center" title="Linked to multiple tasks">
                            <LinkIcon size={12} className="text-gray-400"/>
                            <span className="text-[10px] font-bold text-gray-500">{files.filter(f => f.path === file.path).length}</span>
                        </div>
                    )}
                </div>
            ))}
            
            {uniqueFiles.length === 0 && (
                <div className="col-span-full py-12 text-center border-2 border-dashed border-gray-200 rounded-lg">
                    <p className="text-gray-400">No documents uploaded yet.</p>
                </div>
            )}
        </div>
        </div>
        
        {/* Preview Modal */}
        {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </>
  );
};
export default FileHandler;